import { createContext, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
	CONFIRMATION_SENT_MESSAGE,
	SESSION_EXPIRED_ERROR,
	SIGN_IN_SUCCESS_MESSAGE,
	SIGN_UP_SUCCESS_MESSAGE,
} from './auth-messages';

import { useLoadingState } from '@/hooks/auth/useAuthState';
import { AuthRoutes } from '@/interfaces/auth/AuthRoutes.enum';
import { IAuthenticationContext } from '@/interfaces/auth/IAuthenticationContext';
import { StoredCookies } from '@/interfaces/auth/cookies.enum';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';
import { apiService } from '@/services/api.service';
import { authService } from '@/services/auth.service';
import { cookieService } from '@/services/cookie.service';
import { notificationService } from '@/services/notification.service';

export const AuthContext = createContext<IAuthenticationContext | null>(null);
type PropTypes = { children: React.ReactNode };
export const AuthProvider = ({ children }: PropTypes) => {
	const { loadingState, setLoadingState } = useLoadingState();
	const navigate = useNavigate();
	const location = useLocation();

	// Add function to save query parameters to cookies
	const saveQueryParams = useCallback(() => {
		const queryParams = new URLSearchParams(location.search);
		if (queryParams) {
			cookieService.setRedirectQueryParams(queryParams.toString());
		}
	}, [location.search]);

	// Add function to retrieve and clear saved query parameters
	const getAndClearQueryParams = useCallback(() => {
		const queryParams = cookieService.getCookie(
			StoredCookies.REDIRECT_QUERY_PARAMS,
		);
		if (queryParams) {
			cookieService.remove(StoredCookies.REDIRECT_QUERY_PARAMS);
			return queryParams;
		}
		return '';
	}, []);

	const handleSignIn = useCallback(
		(username: string, password: string, signUpAsConsumer?: boolean) => {
			async function signIn(
				username: string,
				password: string,
				signUpAsConsumer?: boolean,
			) {
				setLoadingState('signIn', true);
				try {
					const { accessToken, refreshToken } = await authService.signIn(
						username,
						password,
						signUpAsConsumer,
					);
					cookieService.setAccessTokenCookie(accessToken);
					cookieService.setRefreshTokenCookie(refreshToken);
					cookieService.setUsernameCookie(username);
					apiService.setAuthentication(accessToken);
					notificationService.success(SIGN_IN_SUCCESS_MESSAGE);

					// Get saved query params and navigate with them if they exist
					const queryParams = getAndClearQueryParams();

					// Navigate based on user type
					let redirectPath: string;
					if (signUpAsConsumer) {
						redirectPath = AppRoutes.ConsumerDashboard;
					} else {
						redirectPath = queryParams
							? `${AppRoutes.ShopifyOAuth}?${queryParams}`
							: AppRoutes.UserDashboard;
					}

					navigate(redirectPath);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when requesting user confirmation: ${error}`,
						);
					}
				} finally {
					setLoadingState('signIn', false);
				}
			}
			return signIn(username, password, signUpAsConsumer);
		},
		[setLoadingState, navigate, getAndClearQueryParams],
	);

	const handleSignInConsumer = useCallback(
		(username: string, password: string) => {
			return handleSignIn(username, password, true);
		},
		[handleSignIn],
	);

	const handleSignUp = useCallback(
		(username: string, password: string, signUpAsConsumer?: boolean) => {
			async function signUp(
				username: string,
				password: string,
				signUpAsConsumer?: boolean,
			) {
				setLoadingState('signUp', true);
				try {
					await authService.signUp(username, password, signUpAsConsumer);
					notificationService.success(CONFIRMATION_SENT_MESSAGE);
					notificationService.success(SIGN_UP_SUCCESS_MESSAGE);
					navigate(AuthRoutes.ConfirmUser);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when signing up: ${error}`,
						);
					}
				} finally {
					setLoadingState('signUp', false);
				}
			}
			return signUp(username, password, signUpAsConsumer);
		},
		[setLoadingState, navigate],
	);

	const handleConfirmUser = useCallback(
		(username: string, code: string) => {
			async function confirmUser(username: string, code: string) {
				setLoadingState('confirmUser', true);
				try {
					const response = await authService.confirmUser(username, code);
					notificationService.success(response.message);
					navigate(AuthRoutes.SignIn);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when requesting user confirmation: ${error}`,
						);
					}
				} finally {
					setLoadingState('confirmUser', false);
				}
			}
			return confirmUser(username, code);
		},
		[setLoadingState, navigate],
	);

	const handleConfirmInvite = useCallback(
		(email: string, otp: string, password: string) => {
			async function confirmInvite() {
				setLoadingState('confirmUser', true);
				try {
					const response = await authService.confirmInvite(
						email,
						otp,
						password,
					);
					notificationService.success(response.message);
					navigate(AuthRoutes.SignIn);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when confirming invite: ${error}`,
						);
					}
				} finally {
					setLoadingState('confirmUser', false);
				}
			}
			return confirmInvite();
		},
		[setLoadingState, navigate],
	);

	const handleSignOut = useCallback(() => {
		cookieService.removeAll();
		window.location.reload();
	}, []);

	const handleForgotPassword = useCallback(
		(username: string) => {
			async function forgotPassword(username: string) {
				setLoadingState('forgotPassword', true);
				try {
					const response = await authService.forgotPassword(username);
					notificationService.success(response.message);
					navigate(AuthRoutes.ConfirmPassword);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when requesting password change: ${error}`,
						);
					}
				} finally {
					setLoadingState('forgotPassword', false);
				}
			}
			return forgotPassword(username);
		},
		[setLoadingState],
	);

	const handleConfirmPassword = useCallback(
		(username: string, newPassword: string, code: string) => {
			async function confirmPassword(
				username: string,
				newPassword: string,
				code: string,
			) {
				setLoadingState('confirmPassword', true);
				try {
					const response = await authService.confirmPassword(
						username,
						newPassword,
						code,
					);
					notificationService.success(response.message);
					navigate(AuthRoutes.SignIn);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when requesting password change confirmation: ${error}`,
						);
					}
				} finally {
					setLoadingState('confirmPassword', false);
				}
			}
			return confirmPassword(username, newPassword, code);
		},
		[setLoadingState, navigate],
	);

	const handleResendConfirmationCode = useCallback(
		(username: string) => {
			async function resendConfirmationCode(username: string) {
				setLoadingState('resendConfirmationCode', true);
				try {
					const response = await authService.resendConfirmationCode(username);
					notificationService.success(response.message);
					navigate(AuthRoutes.ConfirmUser);
				} catch (error: unknown) {
					if (error instanceof Error) {
						notificationService.error(error.message);
					} else {
						notificationService.error(
							`Unknown error when requesting password change confirmation: ${error}`,
						);
					}
				} finally {
					setLoadingState('resendConfirmationCode', false);
				}
			}
			return resendConfirmationCode(username);
		},
		[setLoadingState, navigate],
	);

	const handleRefreshSession = useCallback(async () => {
		setLoadingState('refreshSession', true);
		try {
			const username = cookieService.getCookie(StoredCookies.USERNAME) || '';
			const accessToken =
				cookieService.getCookie(StoredCookies.ACCESS_TOKEN) || '';
			const refreshToken =
				cookieService.getCookie(StoredCookies.REFRESH_TOKEN) || '';
			if (!username || !refreshToken) {
				throw new Error(SESSION_EXPIRED_ERROR);
			}

			if (!accessToken) {
				const response = await authService.refreshToken(username, refreshToken);
				cookieService.setAccessTokenCookie(accessToken);
				apiService.setAuthentication(response.accessToken);
			}
			setLoadingState('refreshSession', false);
		} catch (error) {
			// Save query parameters before redirecting
			saveQueryParams();

			navigate(AppRoutes.SignIn);

			if (error instanceof Error) notificationService.error(error.message);
			else
				notificationService.error(
					'Unexpected error while refreshing your session.\nPlease sign in again.',
				);
		}
	}, [setLoadingState, navigate, saveQueryParams]);

	const contextValue = {
		loadingState,
		handleConfirmPassword,
		handleConfirmUser,
		handleConfirmInvite,
		handleForgotPassword,
		handleRefreshSession,
		handleResendConfirmationCode,
		handleSignIn,
		handleSignInConsumer,
		handleSignOut,
		handleSignUp,
	};
	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};
